import {
  Box, Button, GridItem, Icon, Text,
} from '@chakra-ui/react';
import router from 'next/router';
import React from 'react';
import { FiArrowRight } from 'react-icons/fi';
import formatMoney from '../../../utils/money';
import formatter from '../../../utils/textUtils';
import {
  ICourseGroup,
  ICourseResult,
} from '../../../lib/graphql';
import ItemCard from '../../../components/item/itemCard';
import CourseTypeBadge from '../../../components/CourseTypeBadge';

const CourseList = ({ courses }: { courses: (ICourseResult | ICourseGroup)[] }) => courses?.map((item) => {
  let itemPrice;
  let itemTitle;
  let itemDescription;
  let itemImage;
  let actionButton;
  let imageOverlay;
  let imagePosition;
  let topBar;

  if (!courses) return null;

  switch (item.__typename) {
    case 'CourseGroup': {
      const hasOnline = item.courseTypes?.includes('OnlineCourse');
      const hasStudio = item.courseTypes?.includes('StudioCourse');
      itemTitle = item.name;
      itemDescription = item.shortDescription;
      itemImage = item.image || '';

      // Show discount if minOriginalPrice exists and is greater than minPrice
      const hasDiscount = item.minOriginalPrice && item.minOriginalPrice > item.minPrice;
      if (hasDiscount) {
        const savings = item.minOriginalPrice - item.minPrice;
        itemPrice = (
          <Box lineHeight="1.1">
            <Text
              fontSize="xs"
              color="gray.500"
              textDecoration="line-through"
              mb={0.5}
            >
              {formatMoney(item.minOriginalPrice)}
            </Text>
            <Text fontWeight="bold" fontSize="lg" color="green.600">
              {formatMoney(item.minPrice)}
            </Text>
            <Text fontSize="2xs" color="green.600" fontWeight="500" mt={0.5}>
              Save
              {' '}
              {formatMoney(savings)}
            </Text>
          </Box>
        );
      } else {
        itemPrice = `${formatMoney(item.minPrice)}`;
      }

      imageOverlay = null;
      imagePosition = `${item.imagePosition.X || 50}% ${item.imagePosition.Y}%`;
      // Determine if this course group has online courses

      if (hasOnline && hasStudio) {
        // Mixed group - show up to 3 locations + online
        const locationNames = item.locations?.slice(0, 3).map((location) => formatter.formatLocationName(location)) || [];
        const locationText = locationNames.length > 0 ? locationNames.join(' / ') : '';
        topBar = locationText ? `${locationText} / Online` : 'Online';
      } else if (hasOnline) {
        // Online only
        topBar = 'Online';
      } else {
        // Studio only (default) - show up to 3 locations
        const locationNames = item.locations?.slice(0, 3).map((location) => formatter.formatLocationName(location)) || [];
        topBar = locationNames.length > 0 ? locationNames.join(' / ') : 'Studio';
      }

      // Create badge overlay for image
      imageOverlay = (
        <Box
          position="absolute"
          bottom={3}
          right={3}
          zIndex={2}
        >
          <CourseTypeBadge
            hasOnline={hasOnline}
            hasStudio={hasStudio}
            locationText={topBar}
            size="sm"
          />
        </Box>
      );

      actionButton = (
        <Button
          size={['md', 'md']}
          w="100%"
          onClick={() => router.push(`/course/${item.id}`)}
          colorScheme="yellow"
          variant="solid"
          fontWeight="500"
          borderRadius="lg"
          fontSize={['sm', 'sm']}
          _hover={{
            transform: 'translateY(-1px)',
          }}
          transition="all 0.2s"
          rightIcon={<Icon as={FiArrowRight} />}
        >
          View Course
        </Button>
      );
      break;
    }
    default:
      break;
  }

  return (
    <GridItem key={`bookingList${item.__typename + item.id}`}>
      <ItemCard
        title={itemTitle}
        description={itemDescription}
        image={itemImage}
        ActionButton={actionButton}
        ImageOverlay={imageOverlay}
        imagePosition={imagePosition}
        price={itemPrice}
      />
    </GridItem>
  );
});

export default CourseList;
